import { VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { isEmpty } from 'util/checks'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta06Model,
  ConsultaSeguimentoPergunta06PegarEnum,
} from './model-consultaSeguimentoPergunta06'
import { calculateResultadoPergunta06, conditionToCalculatePergunta06 } from './utils-consultaSeguimentoPergunta06'

interface ConsultaSeguimentoPergunta06FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta06Model>
}

export function ConsultaSeguimentoPergunta06Field(props: Readonly<ConsultaSeguimentoPergunta06FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta06Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta06Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta06,
    calculateResultado: calculateResultadoPergunta06,
  })

  return (
    <VFlow vSpacing={value.PERGUNTA_06_HEADER === SimNaoEnum.SIM ? 0 : 1}>
      {isPerguntaStatusRespondendo(value.status) && (
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_06}
          title='“O seu filho aponta com o dedo quando quer pedir algo ou conseguir ajuda?”'
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_06_HEADER} />}
        />
      )}
      <VFlow vSpacing={0}>
        {value.PERGUNTA_06_HEADER === SimNaoEnum.NAO && (
          <VFlow vSpacing={0}>
            <VFlow>
              <VFlow vSpacing={0}>
                <ConsultaSeguimentoIndicatorStart />
                <ConsultaSeguimentoIndicatorHelper
                  title='Se seu filho quer algo que está fora do alcance dele, como uma comida ou um brinquedo que estejam fora do alcance dele, o que ele faz para conseguir pegar?'
                  subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              </VFlow>
              <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta06PegarEnum>
                name={name.PERGUNTA_06_PEGAR}
                label='Ele...'
                description='Responda os exemplos até pontuar na Pergunta 6 ou avançar para o próximo fluxo'
                options={PERGUNTA_06_OPTIONS}
                required
              />
            </VFlow>
            {hasAnySim(value?.PERGUNTA_06_PEGAR) && (
              <VFlow vSpacing={0}>
                <ConsultaSeguimentoIndicatorStep text='Sim para qualquer um:' />
                <PerguntaSimNaoRadioGroupBoxField
                  name={name.PERGUNTA_06_APONTAR_PARA}
                  label='Se você disser a ele “me mostre”, ele aponta para o objeto?'
                  required
                />
              </VFlow>
            )}
          </VFlow>
        )}
        {value.resultado && (
          <ConsultaSeguimentoIndicatorResultado
            text={{ FALHOU: isEmpty(value?.PERGUNTA_06_APONTAR_PARA) ? 'Não para todos:' : undefined }}
            resultado={value.resultado}
          />
        )}
      </VFlow>
    </VFlow>
  )
}

const PERGUNTA_06_OPTIONS: RadioOption<ConsultaSeguimentoPergunta06PegarEnum>[] = [
  {
    label: 'Tenta alcançar o objeto com a mão?',
    value: ConsultaSeguimentoPergunta06PegarEnum.TENTA_ALCANCAR,
  },
  {
    label: 'Leva você até o objeto?',
    value: ConsultaSeguimentoPergunta06PegarEnum.LEVA_ATE,
  },
  {
    label: 'Tenta pegar o objeto sozinho?',
    value: ConsultaSeguimentoPergunta06PegarEnum.TENTA_PEGAR,
  },
  {
    label: 'Pede usando palavras ou sons?',
    value: ConsultaSeguimentoPergunta06PegarEnum.PEDE,
  },
]
